import type React from 'react'
import { Icon, type IconName } from '@mammoth/design-kit'
import styles from './FacilitiesRow.module.css'

export interface FacilitiesRowProps {
  facilities: ReadonlyArray<string>
}

/**
 * Map a facility token to its iOS-parity icon name. Tokens are passed
 * through from the station schema; the iOS app has its own
 * `Facility.imageName` lookup (see StationDetails/Models/Facility.swift)
 * — both are derived from the same set of asset names. We accept several
 * casings (UPPER_CASE, kebab-case, lowercase-words) so the schema doesn't
 * dictate a specific normalization on the web side.
 *
 * TODO(localization): the iOS app uses `Localizable.strings` to render
 * each facility's label; web localization is out of scope for the V2
 * polish pass, so we display a humanized form of the token.
 */
const FACILITY_ICON_MAP: Record<string, IconName> = {
  AMAZON_LOCKERS: 'facilityAmazonLockers',
  ASDA: 'facilityAsda',
  BIKE_PARKING: 'facilityBikeParking',
  BOARDING_RAMPS: 'facilityBoardingRamps',
  BRIDGE: 'facilityBridge',
  CAR_PARK: 'facilityCarPark',
  CASH_MACHINES: 'facilityCashMachines',
  ESCALATORS: 'facilityEscalators',
  EURO_CASH_MACHINES: 'facilityEuroCashMachines',
  GATES: 'facilityGates',
  HELP_POINTS: 'facilityHelpPoints',
  LIFTS: 'facilityLifts',
  PAYPHONES: 'facilityPayphones',
  TAXI_RANK: 'facilityTaxiRank',
  TICKET_HALLS: 'facilityTicketHalls',
  TOILETS: 'facilityToilets',
  WAITING_ROOM: 'facilityWaitingRoom',
  WIFI: 'facilityWifi',
  WI_FI: 'facilityWifi', // schema also stores `WI-FI` / `Wi-Fi`; both normalize to `WI_FI`
}

function resolveIcon(facility: string): IconName | undefined {
  const upper = facility.replace(/-/g, '_').replace(/\s+/g, '_').toUpperCase()
  // Direct match first; tolerate singular/plural mismatches between
  // schema tokens and iOS asset names (e.g. `lift` ↔ `LIFTS`).
  if (FACILITY_ICON_MAP[upper] !== undefined) return FACILITY_ICON_MAP[upper]
  if (FACILITY_ICON_MAP[upper + 'S'] !== undefined) return FACILITY_ICON_MAP[upper + 'S']
  return undefined
}

/**
 * Irregular-casing exceptions for facility labels. Most tokens follow
 * UPPER_SNAKE_CASE → Title Case, but a handful of brand / acronym names
 * need to stay in their conventional form (Wi-Fi, ASDA). Keep this map
 * tiny — only add entries when a default Title Case looks wrong.
 */
const FACILITY_LABEL_OVERRIDES: Record<string, string> = {
  WIFI: 'Wi-Fi',
  WI_FI: 'Wi-Fi', // schema also stores `WI-FI` / `Wi-Fi`; both normalize to `WI_FI`
  ASDA: 'ASDA',
}

/**
 * Bug 2: schema tokens are lowercased + word-spaced for display, but
 * the previous implementation produced "amazon lockers", "wifi" — too
 * casual. iOS uses `Localizable.strings` to render proper Title Case
 * labels; web doesn't have localization yet, so we humanise on the
 * client. Token forms accepted: UPPER_SNAKE_CASE, lower-kebab-case,
 * already-spaced. Output: Title Case with overrides for irregular
 * names (Wi-Fi, ASDA).
 */
function humanize(token: string): string {
  const upper = token.replace(/-/g, '_').replace(/\s+/g, '_').toUpperCase()
  if (FACILITY_LABEL_OVERRIDES[upper] !== undefined) return FACILITY_LABEL_OVERRIDES[upper]
  return upper
    .toLowerCase()
    .split('_')
    .filter((w) => w.length > 0)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function FacilitiesRow({ facilities }: FacilitiesRowProps): React.ReactElement | null {
  if (facilities.length === 0) return null
  return (
    <ul className={styles.grid} data-testid="station-details-facilities">
      {facilities.map((facility) => {
        const iconName = resolveIcon(facility)
        return (
          <li key={facility} className={styles.cell}>
            {/* The 36×36 facility asset is PRE-COMPOSED: the circular
                `background/secondary` backing is baked into the SVG itself,
                in light and dark variants. Render it whole and at its designed
                size — Figma SD/FacilitiesRow node facility-icon (7000:30605)
                is 36×36, and iOS/Android both draw the asset directly.

                This used to wrap the asset in a second CSS circle and shrink it
                to 20px, so roughly half of those 20px were the backing already
                drawn inside the file and the glyph rendered at about 11px —
                visibly smaller than every neighbouring row's icon (TASK-576). */}
            {iconName !== undefined && (
              <Icon name={iconName} size={36} className={styles.icon} aria-hidden="true" />
            )}
            <span className={styles.label}>{humanize(facility)}</span>
          </li>
        )
      })}
    </ul>
  )
}
